/**
 * PE / Indigo Upfront nudge — monthly frequency cap & per-search session suppression.
 *
 * SSR-safe. READ (checkFrequencyCaps) is split from WRITE (commitFrequencyShow) so a
 * surface decides, renders, then commits exactly once.
 *
 * Cap: 31 / calendar month (localStorage), separate budget for PE and IU.
 * Suppression: interaction-only (tab / fare select / Upgrade CTA), per search query,
 * for the session (sessionStorage). Merely seeing the nudge never suppresses it.
 */
import { INDIGO_UPFRONT_CABIN, isIndigoUpfrontCabin } from '@/constants/flights/cabinClass'
import {
    NUDGE_FREQUENCY_CONFIG,
    NUDGE_KIND,
    NUDGE_SUPPRESSION_STORAGE_KEY,
    normalizeCabinType,
    PE_ALWAYS_SHOW_UPGRADE_CALLOUT_COOKIE,
} from '@/constants/flights/premiumEconomy'
import { getCookie } from '@/utils/general/cookie'
import { isServer } from '@/utils/general'

export type NudgeKind = (typeof NUDGE_KIND)[keyof typeof NUDGE_KIND]

interface PeriodCount {
    key: string
    count: number
}

interface SuppressionEntry {
    query: string
    pe?: boolean
    indigo?: boolean
}

const monthKey = (now: number): string => {
    const d = new Date(now)
    return `${d.getFullYear()}-${d.getMonth() + 1}`
}

function readPeriodCount(storageKey: string, periodKey: string): number {
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) return 0
        const rec: PeriodCount = JSON.parse(raw)
        return rec?.key === periodKey ? rec.count || 0 : 0
    } catch {
        return 0
    }
}

/** Dev/QA cookie: bypass cap + suppression. */
export const isNudgeAlwaysShow = (): boolean => {
    if (isServer()) return false
    try {
        return !!getCookie(PE_ALWAYS_SHOW_UPGRADE_CALLOUT_COOKIE)
    } catch {
        return false
    }
}

const readSuppression = (): SuppressionEntry[] => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(NUDGE_SUPPRESSION_STORAGE_KEY) || '[]')
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

/** Has the user interacted with this kind of upgrade for this search (`query`) this session? */
export function isNudgeSuppressed(kind: NudgeKind, query?: string): boolean {
    if (isServer() || !query || isNudgeAlwaysShow()) return false
    return readSuppression().some((e) => e.query === query && !!e[kind])
}

/** Suppress the nudge for this search only, for the rest of the session. */
export function suppressNudge(kind: NudgeKind, query?: string): void {
    if (isServer() || !query) return
    try {
        const entries = readSuppression()
        const existing = entries.find((e) => e.query === query)
        if (existing) existing[kind] = true
        else entries.push({ query, [kind]: true })
        sessionStorage.setItem(NUDGE_SUPPRESSION_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        /* ignore */
    }
}

/** Pure READ. Omit `query` (itinerary) to apply the cap only. Blocked on the server. */
export function checkFrequencyCaps(kind: NudgeKind = NUDGE_KIND.PE, query?: string, now: number = Date.now()): boolean {
    if (isServer()) return false
    if (isNudgeAlwaysShow()) return true
    try {
        if (isNudgeSuppressed(kind, query)) return false
        const { storageKeys, caps } = NUDGE_FREQUENCY_CONFIG[kind]
        return readPeriodCount(storageKeys.monthly, monthKey(now)) < caps.month
    } catch {
        return false
    }
}

/** WRITE: record one impression. Call once per mount, after the nudge is allowed to render. */
export function commitFrequencyShow(kind: NudgeKind = NUDGE_KIND.PE, now: number = Date.now()): void {
    if (isServer() || isNudgeAlwaysShow()) return
    try {
        const { monthly } = NUDGE_FREQUENCY_CONFIG[kind].storageKeys
        const mk = monthKey(now)
        localStorage.setItem(monthly, JSON.stringify({ key: mk, count: readPeriodCount(monthly, mk) + 1 }))
    } catch {
        /* storage unavailable — nudge simply won't be capped this load */
    }
}

/** PE tab/CTA/fare hides the PE nudge; IU hides the IU nudge — for this search only. */
export const onPremiumFlowInteraction = (cabin?: string | null, query?: string): void => {
    if (!cabin) return
    const normalized = normalizeCabinType(cabin)
    if (normalized === 'PREMIUM_ECONOMY') suppressNudge(NUDGE_KIND.PE, query)
    else if (isIndigoUpfrontCabin(normalized) || normalized === INDIGO_UPFRONT_CABIN) suppressNudge(NUDGE_KIND.INDIGO, query)
}
